import React, { FC, useEffect } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import usePlayProfile from '@fenderdigital/custom-hooks/hooks/useProfile';
import isDomUsable from '@fenderdigital/utils/dom';
import { trackUserIdentity } from '../../lib/analytics';

interface SubscriptionCheckProps {
  lessonSlug?: string;
  courseSlug?: string;
}

const getSignInUrl = (): void => {
  if (isDomUsable()) {
    window.location.href = `${process.env.REACT_APP_CLIENT_SSO_URL}/sign-in?ref=${process.env.REACT_APP_CLIENT_FENDER_COM}${process.env.REACT_APP_CLIENT_PATH_PREFIX}`;
  }
};

const WrapComponent = (WrappedComponent: React.ComponentType<any>): FC<SubscriptionCheckProps> => {
  const SubscriptionCheckFC: FC<SubscriptionCheckProps> = () => {
    const history = useHistory();
    const match = useRouteMatch<{ lessonSlug: string; courseSlug: string }>();
    const params = match?.params;
    const lessonSlug = params?.lessonSlug;
    const courseSlug = params?.courseSlug;
    const location = useLocation();
    const { hasCheckedSession, user, hasSubscription, subscriptionStatus } = usePlayUser();
    const { profileFetched } = usePlayProfile();

    const loggedIn = user && hasCheckedSession;
    const loggedInUnpaid = loggedIn && !hasSubscription;
    const status = subscriptionStatus?.play?.status;
    const resub = loggedIn && status === 'cancelled';
    const { userID } = user || {};

    useEffect(() => {
      if (!hasCheckedSession) return;
      trackUserIdentity(userID);
      if (!loggedIn) getSignInUrl();
      else if (resub) history.push('/plans');
      else if (loggedInUnpaid) {
        if (lessonSlug) history.push(`/lessons/${lessonSlug}`);
        else if (courseSlug) history.push(`/courses/${courseSlug}`);
        else getSignInUrl();
      }
    }, [
      courseSlug,
      hasCheckedSession,
      history,
      lessonSlug,
      loggedIn,
      loggedInUnpaid,
      resub,
      userID,
    ]);
    return loggedIn && profileFetched ? (
      <WrappedComponent loggedIn match={match} history={history} location={location} />
    ) : null;
  };

  return SubscriptionCheckFC;
};

export default WrapComponent;
